import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AccountValues, DeviceValues, accountOptions, deviceOptions } from "Components/Report/OnDemand/Filter";
import { timeOptions } from "./OnDemand/Filter";
import { Moment } from "moment";

interface FilterForm {
  company?: string[];
  time?: (typeof timeOptions)[number]["value"];
  time_period?: any;
  unit: number;
  start_utc?: number;
  end_utc?: number;
  duration?: [Moment, Moment];
  // periodic?: "daily" | "weekly" | "monthly";
  // report_day: number;
  // report_hour: number;
  account_device?: string[];
  account_parameter?: AccountValues[];
  device_device?: string[];
  device_parameter?: DeviceValues[];
}

export interface ReportSlice {
  generatedCharts: string[];
  L1Visible: boolean;
  L2Visible: boolean;
  onViewingDevice: string | undefined;
  onViewingDevice_Period: { start_utc: number; end_utc: number; unit: number } | undefined;
  onEventValues: any;
  resportSetting: Record<
    AccountValues | `${AccountValues}_2` | `${AccountValues}_` | DeviceValues | `${DeviceValues}_2` | any,
    number
  >;
  filterForm: FilterForm;
  preference: {
    column: {
      account: number;
      device: number;
    };
    order: {
      account: AccountValues[][];
      device: DeviceValues[][];
    };
  };
  PDFVisible: boolean;
  isFetchingAPI?:boolean;
}

const accountParameters = accountOptions.map((o) => o.value);
const deviceParameters = deviceOptions.map((o) => o.value);
const getDefault = <T>(paramters: T[]) => {
  const isOdd = paramters.length % 2 === 1;
  const middleIndex = Math.ceil(paramters.length / 2);
  const firstHalf = Array.from(paramters).slice(0, middleIndex);
  const secondHalf = Array.from(paramters).slice(isOdd ? -middleIndex + 1 : -middleIndex);
  return [firstHalf, secondHalf];
};

const DnD_account_storage = localStorage.getItem("report.DnD.accountParam");
const DnD_device_storage = localStorage.getItem("report.DnD.deviceParam");
const accountCache = DnD_account_storage
  ? (JSON.parse(DnD_account_storage) as AccountValues[][])
  : [...getDefault(accountParameters)];
const deviceCache = DnD_device_storage
  ? (JSON.parse(DnD_device_storage) as DeviceValues[][])
  : [...getDefault(deviceParameters)];

export const initialState: ReportSlice = {
  generatedCharts: [],
  L1Visible: false,
  L2Visible: false,
  onEventValues: undefined,
  resportSetting: JSON.parse(localStorage.getItem("resport.chart.setting") || "{}"),
  onViewingDevice: undefined, //onViewing device of account level
  onViewingDevice_Period: undefined,
  filterForm: {
    company: [],
    time: 604800,
    time_period: "",
    unit: 86400,
    // periodic: "daily",
    // report_day: 1,
    // report_hour: 0,
    account_device: [],
    account_parameter: [],
    device_device: [],
    device_parameter: [],
  },
  preference: {
    column: {
      account: 2,
      device: 2,
    },
    order: {
      account: accountCache,
      device: deviceCache,
    },
  },
  PDFVisible: false,
  isFetchingAPI:false
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    setGeneratedCharts: (state, action: PayloadAction<string[]>) => {
      state.generatedCharts = action.payload;
    },
    setL1Visible: (state, action: PayloadAction<ReportSlice["L1Visible"]>) => {
      state.L1Visible = action.payload;
    },
    setL2Visible: (state, action: PayloadAction<ReportSlice["L2Visible"]>) => {
      state.L2Visible = action.payload;
    },
    setOnEventValues: (state, action: PayloadAction<ReportSlice["onEventValues"]>) => {
      state.onEventValues = action.payload;
    },
    setResportSetting: (state, action: PayloadAction<ReportSlice["resportSetting"]>) => {
      state.resportSetting = action.payload;
    },
    setFilterForm: (state, action: PayloadAction<ReportSlice["filterForm"]>) => {
      state.filterForm = action.payload;
    },

    setOnViewDevice: (state, action: PayloadAction<ReportSlice["onViewingDevice"]>) => {
      state.onViewingDevice = action.payload;
    },

    setOnViewDevice_Period: (state, action: PayloadAction<ReportSlice["onViewingDevice_Period"]>) => {
      state.onViewingDevice_Period = action.payload;
    },

    setPreference: (state, action: PayloadAction<ReportSlice["preference"]>) => {
      state.preference = action.payload;
    },

    setPDFVisible: (state, action: PayloadAction<ReportSlice["PDFVisible"]>) => {
      state.PDFVisible = action.payload;
    },
    setIsFetchingAPI: (state, action: PayloadAction<boolean>) => {
      state.isFetchingAPI = action.payload;
    },
  },
});

export const {
  setGeneratedCharts,
  setL1Visible,
  setL2Visible,
  setOnEventValues,
  setOnViewDevice_Period,
  setResportSetting,
  setFilterForm,
  setOnViewDevice,
  setPreference,
  setPDFVisible,
  setIsFetchingAPI
} = reportSlice.actions;

export default reportSlice;
